import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { CollegePortalService } from '../../../../college-workspace/services/college-portal.service';
import { AddStudentPayload, BulkAddStudentsResult } from '../../../../college-workspace/models/college-portal.models';
import { phoneValidator } from '../../../../hiring-workspace/recruiter-auth/recruiter-auth.component';

const CSV_COLUMNS = ['first_name', 'last_name', 'email', 'phone_number', 'course', 'city', 'year_of_passing'];

@Component({
  selector: 'app-add-student-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './add-student-modal.component.html',
  styleUrl: './add-student-modal.component.scss'
})
export class AddStudentModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() studentAdded = new EventEmitter<void>();

  activeTab: 'manual' | 'csv' = 'manual';
  isSubmitting = false;
  collegeName = '';
  courseOptions: string[] = [];
  csvColumns = CSV_COLUMNS;

  form = new FormGroup({
    first_name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    last_name: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    phone_number: new FormControl('', [Validators.required, phoneValidator()]),
    course: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    city: new FormControl(''),
    year_of_passing: new FormControl('', [Validators.required, Validators.min(1950), Validators.max(2100)]),
    send_invite: new FormControl(true)
  });

  // CSV tab state
  selectedFile: File | null = null;
  isUploading = false;
  bulkResult: BulkAddStudentsResult | null = null;

  private subscription = new Subscription();

  constructor(
    private collegeService: CollegePortalService,
    private toastr: ToastrService
  ) {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.subscription.add(this.collegeService.getCollegeProfile().subscribe({
      next: (res) => {
        if (res.Success) {
          this.collegeName = res.Data.college_name;
          this.courseOptions = Array.from(new Set([...(res.Data.streams || []), ...(res.Data.courses_offered || [])]));
          if (!this.courseOptions.length) {
            // The select stays disabled with nothing valid to pick — don't
            // block submission on a field the admin has no way to fill in.
            const courseControl = this.form.get('course');
            courseControl?.clearValidators();
            courseControl?.updateValueAndValidity();
          }
          if (res.Data.city) {
            this.form.patchValue({ city: res.Data.city });
          }
        }
      },
      error: () => {
        // Leave blank — informational field, not required to submit.
      }
    }));
  }

  switchTab(tab: 'manual' | 'csv'): void {
    this.activeTab = tab;
  }

  // ─────────────────────────────────────────────
  // Manual entry
  // ─────────────────────────────────────────────

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.value;
    const payload: AddStudentPayload = {
      first_name: (v.first_name || '').trim(),
      last_name: (v.last_name || '').trim(),
      email: (v.email || '').trim(),
      phone_number: (v.phone_number || '').trim(),
      course: (v.course || '').trim(),
      city: (v.city || '').trim() || undefined,
      year_of_passing: Number(v.year_of_passing),
      send_invite: !!v.send_invite
    };

    this.isSubmitting = true;
    this.collegeService.addStudent(payload).pipe(
      finalize(() => { this.isSubmitting = false; })
    ).subscribe({
      next: (res) => {
        if (res.Success) {
          this.toastr.success(res.Message || 'Student added successfully.');
          this.studentAdded.emit();
          this.close.emit();
        } else {
          this.toastr.error(res.Message || 'Failed to add student.');
        }
      },
      error: (err) => {
        this.toastr.error(err?.error?.Message || 'Failed to add student.');
      }
    });
  }

  // ─────────────────────────────────────────────
  // CSV upload
  // ─────────────────────────────────────────────

  downloadTemplate(): void {
    const csvContent = this.csvColumns.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'student_upload_template.csv');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.toastr.error('Please upload a .csv file.');
      input.value = '';
      return;
    }

    this.selectedFile = file;
    this.bulkResult = null;
  }

  removeSelectedFile(): void {
    this.selectedFile = null;
    this.bulkResult = null;
  }

  uploadCsv(): void {
    if (!this.selectedFile) {
      this.toastr.warning('Please select a CSV file first.');
      return;
    }

    this.isUploading = true;
    this.collegeService.addStudentsBulk(this.selectedFile, !!this.form.value.send_invite).pipe(
      finalize(() => { this.isUploading = false; })
    ).subscribe({
      next: (res) => {
        if (res.Success) {
          this.bulkResult = res.Data;
          this.toastr.success(res.Message || 'File processed.');
          if (res.Data.created > 0) {
            this.studentAdded.emit();
          }
        } else {
          this.toastr.error(res.Message || 'Failed to process the uploaded file.');
        }
      },
      error: (err) => {
        this.toastr.error(err?.error?.Message || 'Failed to process the uploaded file.');
      }
    });
  }
}
